/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * Selectors for Labels Import
 *
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @version 1.11.0
 * @since 1.3.0
 */

import { createSelector, OutputSelector } from 'reselect';

import { IRootReducer } from '../../reducers/rootReducer';
import { ELabelsImportSteps } from './labelsImportConstants';
import {
    ILabelsImportFileInfoItem,
    ILabelsImportFileMeta,
    ILabelsImportLabelsValidationInfo,
    ILabelsImportReducer,
} from './labelsImportReducer';

export const makeSelectLabelsImportState = (state: IRootReducer): ILabelsImportReducer => state.labelsImport;

/*
* -------------------------------------------
*           META
* -------------------------------------------
*/

export const makeSelectLabelsImportFileMeta = (): OutputSelector<IRootReducer, ILabelsImportFileMeta,
    (state: ILabelsImportReducer) => ILabelsImportFileMeta> =>
    createSelector(makeSelectLabelsImportState, state => state[ELabelsImportSteps.File]);

/*
* -------------------------------------------
*           FILE
* -------------------------------------------
*/

export const makeSelectLabelsImportFileError = (): OutputSelector<IRootReducer, string,
    (state: ILabelsImportReducer) => string> =>
    createSelector(makeSelectLabelsImportState, state => state.fileError);

export const makeSelectLabelsImportFileInfoItems = (): OutputSelector<IRootReducer, ILabelsImportFileInfoItem[],
    (state: ILabelsImportReducer) => ILabelsImportFileInfoItem[]> =>
    createSelector(makeSelectLabelsImportState, state => state.fileInfoItems);

export const makeSelectLabelsImportFileObjects = (): OutputSelector<IRootReducer, File[],
    (state: ILabelsImportReducer) => File[]> =>
    createSelector(makeSelectLabelsImportState, state => state.fileObjects);

export const makeSelectLabelsImportIsFileUploaded = (): OutputSelector<IRootReducer, boolean,
    (state: ILabelsImportReducer) => boolean> =>
    createSelector(makeSelectLabelsImportState, state => state.isFileUploaded);

export const makeSelectLabelsImportIsMaxRowsExceeded = (): OutputSelector<IRootReducer, boolean,
    (state: ILabelsImportReducer) => boolean> =>
    createSelector(makeSelectLabelsImportState, state => state.isMaxRowsExceeded);

export const makeSelectLabelsImportMaxCountSubscribers = (): OutputSelector<IRootReducer, number,
    (state: ILabelsImportReducer) => number> => createSelector(makeSelectLabelsImportState,
        state => state[ELabelsImportSteps.File].maxRows);

/*
* -------------------------------------------
*           IS STEP VALID
* -------------------------------------------
*/

export const makeSelectLabelsImportIsStepValid = (): OutputSelector<IRootReducer, boolean,
    (state: ILabelsImportReducer) => boolean> =>
    createSelector(makeSelectLabelsImportState, state => state.isStepValid);

export const makeSelectLabelsImportValidateError = (): OutputSelector<IRootReducer, string,
    (state: ILabelsImportReducer) => string> =>
    createSelector(makeSelectLabelsImportState, state => state.validateError);

/*
* -------------------------------------------
*           LABELS VALIDATION
* -------------------------------------------
*/

export const makeSelectLabelsImportLabelsValidationInfo = (): OutputSelector<IRootReducer,
    ILabelsImportLabelsValidationInfo, (state: ILabelsImportReducer) => ILabelsImportLabelsValidationInfo> =>
    createSelector(makeSelectLabelsImportState, state => state.labelsValidationInfo);

export const makeSelectLabelsImportTotalRowsCount = (): OutputSelector<IRootReducer,
    number, (state: ILabelsImportReducer) => number> =>
    createSelector(makeSelectLabelsImportState, state => state.totalRowsCount);


/*
* -------------------------------------------
*           STORAGE
* -------------------------------------------
*/

export const makeSelectLabelsImportLabelsIsDataRestoredFromStorage = (): OutputSelector<IRootReducer,
    boolean, (state: ILabelsImportReducer) => boolean> =>
    createSelector(makeSelectLabelsImportState, state => state.isDataRestoredFromStorage);
